import React, { useEffect, useId, useRef } from 'react';
import type { OtpInputProps } from './types';

// ─── OtpInput ─────────────────────────────────────────────────────────────────

export function OtpInput({
  length = 6,
  value = '',
  onChange,
  error,
  disabled = false,
  autoFocus = true,
  onComplete,
}: OtpInputProps) {
  const errorId = useId();
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  // Normalise value into a fixed-length digit array
  const digits = Array.from({ length }, (_, i) => value[i] ?? '');

  // Auto-focus first cell on mount
  useEffect(() => {
    if (autoFocus && !disabled) {
      inputRefs.current[0]?.focus();
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  function notify(next: string[]) {
    const joined = next.join('');
    onChange?.(joined);
    if (joined.length === length && next.every(Boolean)) {
      onComplete?.(joined);
    }
  }

  function handleChange(index: number, e: React.ChangeEvent<HTMLInputElement>) {
    const raw = e.target.value.replace(/\D/g, '');
    if (!raw) return;
    // Take last digit typed (handles Android auto-fill that replaces full value)
    const digit = raw[raw.length - 1];
    const next = [...digits];
    next[index] = digit;
    notify(next);
    // Move focus to next cell
    if (index < length - 1) {
      inputRefs.current[index + 1]?.focus();
    }
  }

  function handleKeyDown(index: number, e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Backspace') {
      e.preventDefault();
      if (digits[index]) {
        // Clear current cell
        const next = [...digits];
        next[index] = '';
        notify(next);
      } else if (index > 0) {
        // Move to previous and clear it
        const next = [...digits];
        next[index - 1] = '';
        notify(next);
        inputRefs.current[index - 1]?.focus();
      }
    } else if (e.key === 'ArrowLeft' && index > 0) {
      e.preventDefault();
      inputRefs.current[index - 1]?.focus();
    } else if (e.key === 'ArrowRight' && index < length - 1) {
      e.preventDefault();
      inputRefs.current[index + 1]?.focus();
    }
  }

  function handlePaste(e: React.ClipboardEvent<HTMLInputElement>) {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, length);
    if (!pasted) return;
    const next = Array.from({ length }, (_, i) => pasted[i] ?? '');
    notify(next);
    // Focus last filled cell or last cell
    const lastFilledIndex = Math.min(pasted.length - 1, length - 1);
    inputRefs.current[lastFilledIndex]?.focus();
  }

  function handleFocus(e: React.FocusEvent<HTMLInputElement>) {
    // Select text so re-typing replaces existing digit
    e.target.select();
  }

  const hasError = Boolean(error);

  const cellBase =
    'w-12 h-12 text-center text-lg font-bold rounded border outline-none transition-colors ' +
    'focus-visible:ring-2 focus-visible:ring-offset-1 ';

  const cellState = hasError
    ? 'border-danger-500 focus-visible:ring-danger-500 '
    : 'border-neutral-300 dark:border-neutral-600 focus-visible:ring-brand-500 focus-visible:border-brand-500 ';

  const cellDisabled = disabled
    ? 'bg-neutral-200 dark:bg-neutral-700 text-neutral-400 cursor-not-allowed pointer-events-none '
    : 'bg-white dark:bg-neutral-900 text-neutral-900 dark:text-neutral-100 ';

  const cellFilled = 'font-bold ';

  return (
    <div className="flex flex-col gap-2">
      <div
        className="flex items-center gap-2"
        role="group"
        aria-label={`Enter ${length}-digit code`}
        aria-describedby={hasError ? errorId : undefined}
      >
        {digits.map((digit, i) => (
          <input
            key={i}
            ref={(el) => {
              inputRefs.current[i] = el;
            }}
            type="text"
            inputMode="numeric"
            maxLength={1}
            value={digit}
            disabled={disabled}
            autoComplete="one-time-code"
            aria-label={`Digit ${i + 1} of ${length}`}
            aria-describedby={hasError ? errorId : undefined}
            aria-invalid={hasError}
            className={[cellBase, cellState, cellDisabled, digit ? cellFilled : ''].join('')}
            onChange={(e) => handleChange(i, e)}
            onKeyDown={(e) => handleKeyDown(i, e)}
            onPaste={handlePaste}
            onFocus={handleFocus}
          />
        ))}
      </div>

      {hasError && (
        <span id={errorId} role="alert" className="text-xs text-danger-600 dark:text-danger-400">
          {error}
        </span>
      )}
    </div>
  );
}
